import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { IREIGraphDataSet } from "../../../interface/graph";
import { IEntity, IEquity } from "../../../interface/propertyTracker";
import { REIProgressSpinner } from "../../../components/ui";
import REIToggablePanel from "../../../components/ui/toggablePanel";
import {
  formatNumber,
  formatYAxisTick,
  formatYearAsFY,
  getCurrentFinancialYear,
  makeSplitColorFn,
  segmentColorBySplit,
} from "../../../utils/commonUtil";
import ScrollableGraph from "../../../components/shared/ScrollableGraph";
import { useNavigate } from "react-router-dom";
import { IActual4 } from "../../../interface/myPortfolio";

const Equity: React.FC<{
  entityId: string;
  showingTotalSummary: boolean;
  year?: number;
  onYearChangeCallback?: (year: number) => void;
}> = ({ entityId, showingTotalSummary, year, onYearChangeCallback }) => {
  const trackerData = useSelector((state: RootState) => state.myTracker.trackerData);
  const isLoading = useSelector((state: RootState) => state.myTracker.loading);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentYearIndex, setCurrentYearIndex] = useState(0);
  const [chartData, setChartData] = useState<IREIGraphDataSet[]>([
    { label: "MarketValue", data: [], backgroundColor: ["rgba(45, 212, 191, 1)"] },
    { label: "LoanBalance", data: [], backgroundColor: ["rgba(156, 163, 175, 1)"] },
  ]);
  const [trakerIndexData, setTrakerIndexData] = useState<IEntity[]>();
  const [equityData, setEquityData] = useState<{ Actuals: IActual4[]; Forecast: IActual4[] }>();
  const [selectedTrakerIndexData, setSelectedTrakerIndexData] = useState<IEntity>();
  const [years, setYears] = useState<string[]>([]);
  const [selectedYear, setSelectedYear] = useState<number>();
  const [selectedYearForecast, setSelectedYearForecast] = useState<IActual4>();
  const displayedYears = 5;
  const colorMap = {
    MarketValue: {
      0: "rgba(240, 171, 252, 1)",
      1: "rgba(240, 171, 252, 1)",
      default: "rgba(45, 212, 191, 1)",
    },
    LoanBalance: {
      0: "rgba(56, 189, 248, 1)",
      1: "rgba(56, 189, 248, 1)",

      default: "rgba(156, 163, 175, 1)",
    },
  };
  const currentYear = Number(new Date().getFullYear().toString());
  const navigate = useNavigate();
  const actualData = useRef<IEquity[]>([]);
  const forecastData = useRef<IEquity[]>([]);
  const isCurrentFinancialYear = selectedYear?.toString()?.slice(2, 4) === getCurrentFinancialYear();

  useEffect(() => {
    const getTrackerData = async () => {
      setTrakerIndexData((prevState) => {
        return trackerData;
      });
    };
    if (trackerData) {
      getTrackerData();
    }
  }, [trackerData, entityId, showingTotalSummary]);

  useEffect(() => {
    setLoading(true);
    getChartData();
    setLoading(false);
  }, [trakerIndexData, currentYearIndex, displayedYears]);

  useEffect(() => {
    years && setSelectedYear((prev) => Number(years[currentYearIndex]));
    if (equityData && years.length > 0) {
      const combinedData: IActual4[] = [];
      // const actual = selectedTrakerIndexData.Actuals;
      if (actualData.current.length > 0) {
        actualData.current.forEach((act) => {
          combinedData.push(act);
        });
      }
      forecastData.current?.forEach((forecast, index) => {
        if (forecast.Year !== actualData.current[index]?.Year) {
          combinedData.push(forecast);
        }
      });
      currentYearIndex <= actualData.current.length - 1
        ? setSelectedYearForecast(actualData.current[currentYearIndex])
        : setSelectedYearForecast(combinedData[currentYearIndex]);
    }
  }, [equityData, currentYearIndex]);

  useEffect(() => {
    if (year !== undefined && year !== selectedYear && years.length > 0) {
      setSelectedYear((prev) => year);
      setCurrentYearIndex((prev) => years.indexOf(year.toString()));
    }
  }, [year, years]);

  const getChartData = () => {
    const data = trakerIndexData?.find((x) => x.EntityId === entityId);
    if (data && (data.EquityTotals != null || data.EquityPortions != null)) {
      forecastData.current = showingTotalSummary ? data.EquityTotals.Forecast : data.EquityPortions.Forecast;
      actualData.current = showingTotalSummary ? data.EquityTotals.Actuals : data.EquityPortions.Actuals;

      const actualLength = actualData.current.length;

      const actualMap = actualData.current.reduce((acc, curr) => {
        acc[curr.Year] = curr;
        return acc;
      }, {} as Record<string, IActual4>);

      const forecastMap = forecastData.current.reduce((acc, curr) => {
        acc[curr.Year] = curr;
        return acc;
      }, {} as Record<string, IActual4>);

      const allYears = Array.from(
        new Set([...forecastData.current.map((f) => f.Year), ...actualData.current.map((a) => a.Year)])
      ).sort();

      const datasets = [
        {
          label: "Market Value",
          key: "MarketValue",
          actualColor: "rgb(170, 0, 0)",
          forecastColor: "rgb(102, 119, 136)",
          actualLength,
        },
        {
          label: "Loan Balance",
          key: "LoanValue",
          actualColor: "rgb(0, 0, 170)",
          forecastColor: "rgb(0, 170, 0)",
          actualLength,
        },
      ];

      const finalChartData = datasets.map(({ label, key, actualColor, forecastColor, actualLength }) => ({
        label,
        data: allYears.map((year) => {
          const value = actualMap[year]?.[key] ?? forecastMap[year]?.[key];
          return value != null ? parseFloat(value) : null;
        }),
        borderWidth: 3,
        pointRadius: 2,
        tension: 0.4,
        fill: false,
        segment: segmentColorBySplit(actualColor, forecastColor, actualLength),
        pointBackgroundColor: makeSplitColorFn(actualColor, forecastColor, actualLength),
        pointBorderColor: makeSplitColorFn(actualColor, forecastColor, actualLength),
      }));

      setChartData(finalChartData);
      setYears(allYears);
      setEquityData((prev) => ({
        ...prev,
        Actuals: actualData.current || [],
        Forecast: forecastData.current || [],
      }));
    }
  };

  const customOptions = {
    plugins: {
      maintainAspectRatio: false,
      legend: {
        display: false,
        position: "top",
      },
    },
    scales: {
      y: {
        ticks: {
          callback: formatYAxisTick,
        },
        grid: {
          display: false,
        },
        beginAtZero: true,
      },
      x: {
        offset: isCurrentFinancialYear,
        ticks: {
          callback: (value: number, index: number) => {
            const actualIndex = currentYearIndex + index;

            if (years.length > 0 && actualIndex < years.length) {
              return formatYearAsFY(years[actualIndex]);
            }
            return value;
          },
        },
        grid: {
          display: false,
        },
      },
    },
  };

  const CashFlowHeader = (
    <div className="flex flex-row bg-slate-600 border-solid border-gray-200 border-x border-y rounded-t-md ">
      <div className="w-1/2 text-start text-white font-bold text-sm ml-auto pl-3">
        <h3>Equity</h3>
      </div>
      <div className="w-1/2 text-end pl-3 content-center px-4">
        <a
          id="mteq-lnk-viewbyproperty"
          data-testid="mteq-lnk-viewbyproperty"
          onClick={() => {
            navigate(`/portfolio-tracker`, {
              state: {
                tab: "MyEquityTab",
              },
            });
          }}
          className="no-underline cursor-pointer text-white"
        >
          {"View by property >>"}
        </a>
      </div>
    </div>
  );

  const getClassForValue = (value: string | undefined) => {
    return Number(value) < 0 ? "text-red-500" : "";
  };

  const renderGraphHeader = (
    <div className="flex flex-row justify-around pt-2">
      <div className="flex w-1/4 content-end items-center justify-center">
        <b>
          {` FY-${selectedYear?.toString().slice(2, 4)} `}
          {isCurrentFinancialYear
            ? "Actuals YTD"
            : Number(selectedYear?.toString().slice(2, 4)) < Number(getCurrentFinancialYear())
            ? "Actuals"
            : "Forecast"}
        </b>
      </div>
      <div className="overflow-x-auto w-full">
        <div className="p-2 flex flex-col text-sm content-center bg-gray-100 border-solid border-gray-200 border-x border-y rounded-md  min-w-[500px]">
          <table className="w-full table-auto">
            <thead>
              <tr className="flex justify-between text-center w-full">
                <th className="w-1/4 px-2">Market Value</th>
                <th className="w-1/4 px-2">Loan Balance</th>
                <th className="w-1/4 px-2">Equity</th>
              </tr>
            </thead>
            <tbody>
              <tr className="flex justify-between text-center w-full">
                <td className={`w-1/4 ${getClassForValue(selectedYearForecast?.MarketValue)}`}>
                  <b>
                    {selectedYearForecast?.MarketValue !== undefined &&
                    !isNaN(Number(selectedYearForecast?.MarketValue))
                      ? Number(selectedYearForecast?.MarketValue) < 0
                        ? `($ ${formatNumber(Math.abs(Number(selectedYearForecast?.MarketValue)))})`
                        : `$ ${formatNumber(Number(selectedYearForecast?.MarketValue))}`
                      : "-"}
                  </b>
                </td>
                <td className={`w-1/4 ${getClassForValue(selectedYearForecast?.LoanValue)}`}>
                  <b>
                    {selectedYearForecast?.LoanValue !== undefined && !isNaN(Number(selectedYearForecast?.LoanValue))
                      ? Number(selectedYearForecast?.LoanValue) < 0
                        ? `($ ${formatNumber(Math.abs(Number(selectedYearForecast?.LoanValue)))})`
                        : `$ ${formatNumber(Number(selectedYearForecast?.LoanValue))}`
                      : "-"}
                  </b>
                </td>
                <td className={`w-1/4 ${getClassForValue(selectedYearForecast?.Equity)}`}>
                  {selectedYearForecast?.Equity !== undefined && !isNaN(Number(selectedYearForecast?.Equity)) ? (
                    <b>
                      <span className={getClassForValue(selectedYearForecast.Equity)}>
                        {Number(selectedYearForecast.Equity) < 0
                          ? `($ ${formatNumber(Math.abs(Number(selectedYearForecast.Equity)))})`
                          : `$ ${formatNumber(Number(selectedYearForecast.Equity))}`}
                      </span>
                    </b>
                  ) : (
                    "-"
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderGraphLegend = (
    <div className="bg-gray-100 border-solid border-gray-200 border-x border-y rounded-b-md p-3 m-3">
      <div className="flex justify-around pl-1">
        <div className="flex gap-2 items-center">
          <span className="w-4 h-4 bg-red-700"></span>
          <span className="text-center">Actual market value</span>
        </div>
        <div className="flex gap-2 items-center">
          <div className="w-4 h-4 bg-blue-800"></div>
          <span className="text-center">Actual loan balance</span>
        </div>
        <div className="flex gap-2 items-center">
          <div className="w-4 h-4 bg-slate-500"></div>
          <span className="text-center">Forecast market value</span>
        </div>
        <div className="flex gap-2 items-center">
          <div className="w-4 h-4 bg-green-600"></div>
          <span className="text-center">Forecast loan balance</span>
        </div>
      </div>
    </div>
  );

  const onYearChange = (selectedYearIndex: number) => {
    setCurrentYearIndex((prevData) => selectedYearIndex);
    if (onYearChangeCallback) {
      onYearChangeCallback(Number(years.find((_, index) => index === selectedYearIndex)?.slice(2, 4)));
    }
  };

  return (
    <>
      {loading && (
        <div className="flex items-center">
          <REIProgressSpinner />
        </div>
      )}

      <REIToggablePanel
        contentComponent={
          <div className="flex flex-col">
            {renderGraphHeader}
            {!loading && chartData[0].data.length != 0 && (
              <div>
                <ScrollableGraph
                  id="mteq-graph-equity"
                  onYearChange={onYearChange}
                  data={chartData}
                  displayedYears={displayedYears}
                  graphType="line"
                  labels={years}
                  customOptions={customOptions}
                  selectedYearIndex={
                    forecastData.current.length != 0 || actualData.current.length != 0 ? currentYearIndex : 0
                  }
                />
                {renderGraphLegend}
              </div>
            )}
          </div>
        }
        header=""
        customHeader={CashFlowHeader}
        istoggleable={false}
        defaultCollapse={false}
      />
    </>
  );
};

export default Equity;
